import { useCallback, useRef } from 'react';

/**
 * useEmojiInput
 *
 * A shared hook for inserting emojis (or any text) at the cursor position
 * in an Ant Design Input or Input.TextArea.
 *
 * Usage:
 *   const { inputRef, saveCursorPosition, insertAtCursor, focusInput } = useEmojiInput();
 *
 *   <Input
 *     ref={inputRef}
 *     onBlur={saveCursorPosition}
 *     onSelect={saveCursorPosition}
 *   />
 *
 *   // On emoji click:
 *   insertAtCursor(emoji.emoji, getCurrentValue, setNewValue, onAfterInsert);
 */
const useEmojiInput = () => {
    const inputRef = useRef(null);
    const cursorPosRef = useRef({ start: 0, end: 0 });

    /**
     * Resolves the underlying native <input> or <textarea> element
     * from an Ant Design ref (which wraps the real element).
     */
    const getNativeEl = () => {
        const ref = inputRef.current;
        if (!ref) return null;
        // Input.TextArea: ref.resizableTextArea.textArea
        // Input: ref.input
        return ref?.resizableTextArea?.textArea || ref?.input || ref;
    };

    /** Save current cursor position — call on onBlur and onSelect */
    const saveCursorPosition = () => {
        const el = getNativeEl();
        if (el) {
            cursorPosRef.current = {
                start: el.selectionStart ?? 0,
                end: el.selectionEnd ?? 0,
            };
        }
    };

    const focusInput = useCallback(() => {
        setTimeout(() => {
            getNativeEl()?.focus();
        }, 0);
    }, []);

    /**
     * Insert `text` at the saved cursor position.
     *
     * @param {string}   text           - The text/emoji to insert
     * @param {string}   currentValue   - The current field value
     * @param {Function} setValue       - Callback to update the value (receives the new string)
     * @param {Function} [onAfterInsert] - Optional callback fired after cursor is repositioned
     */
    const insertAtCursor = (text, currentValue, setValue, onAfterInsert) => {
        const el = getNativeEl();
        const val = el ? el.value : (currentValue || '');
        const start = cursorPosRef.current.start ?? val.length;
        const end = cursorPosRef.current.end ?? start;

        const newValue = val.slice(0, start) + text + val.slice(end);
        setValue(newValue);

        // Restore cursor to just after the inserted text
        const newPos = start + text.length;
        setTimeout(() => {
            if (el) {
                el.selectionStart = el.selectionEnd = newPos;
                el.focus();
            }
            if (onAfterInsert) onAfterInsert(newValue, newPos);
        }, 0);

        // Update saved position so successive inserts are also correct
        cursorPosRef.current = { start: newPos, end: newPos };
    };

    return { inputRef, cursorPosRef, saveCursorPosition, insertAtCursor, focusInput };
};

export default useEmojiInput;
